import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowDownRight,
  BookOpen,
  Compass,
  Trophy,
  Sparkles,
} from "lucide-react";
import { useReveal } from "../lib/useReveal";
import ContactForm from "../components/site/ContactForm";

const programs = [
  {
    n: "01",
    title: "Book Club",
    icon: BookOpen,
    image:
      "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    description:
      "Reading + dialogue. Builds literacy, communication, critical thinking, confidence, and growth — through literature.",
    tag: "READ · THINK · SPEAK",
  },
  {
    n: "02",
    title: "Divine Care Academy",
    icon: Compass,
    image: "/assets/other/community-3.jpg",
    description:
      "Mentorship + leadership. Spiritual growth, life skills, mental health awareness, discipline, integrity, and Christian service.",
    tag: "LEAD · SERVE · GROW",
  },
  {
    n: "03",
    title: "Cadet & Basketball",
    icon: Trophy,
    image: "/assets/basketball/training-1.jpg",
    description:
      "Sports + discipline. Basketball as the classroom for teamwork, fitness, accountability, perseverance, leadership, and respect.",
    tag: "BALL · DISCIPLINE · BROTHERHOOD",
    href: "/basketball-camp",
  },
];

const marqueeItems = [
  "CONNECTING THE DISCONNECTED",
  "✱",
  "PURPOSE",
  "✱",
  "EXCELLENCE",
  "✱",
  "UNDERSTANDING",
  "✱",
];

export default function Home() {
  useReveal();

  return (
    <main data-testid="home-page" className="bg-[#F4EBDC]">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative pt-28 sm:pt-32 pb-0 overflow-hidden bg-[#F4EBDC]"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10">
          {/* top metadata strip */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-6">
            <span className="tag">PROVERBS · 01 : 04</span>
            <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#0E0E0E]/70">
              Established 2021 · LA County · Compton · Carson
            </span>
          </div>
          <span className="tape" />

          <div className="pt-10 sm:pt-14">
            <h1
              className="font-display text-[#0E0E0E] reveal text-center whitespace-nowrap leading-[0.95] tracking-tight"
              style={{ fontSize: "clamp(1.75rem, 7.1vw, 7.25rem)" }}
              data-testid="hero-headline"
            >
              <span className="text-[#0E0E0E]">DIVINE </span>
              <span className="text-[#1B4F72]">CARE</span>
              <span className="text-[#0E0E0E]"> ORGANIZATION</span>
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-10 sm:pt-14 items-start">
            <div className="lg:col-span-7 reveal reveal-delay-1">
              <p
                className="font-display italic text-[#1B4F72] text-2xl sm:text-3xl lg:text-4xl leading-tight"
                data-testid="hero-tagline"
              >
                A care that makes a difference.
              </p>
              <p
                className="mt-6 text-[#0E0E0E] text-base sm:text-lg lg:text-xl leading-snug font-medium max-w-2xl"
                data-testid="hero-subheadline"
              >
                Divine Care Organization is committed to developing youth in{" "}
                <span className="bg-[#FFD700] px-1">spirit</span>,{" "}
                <span className="bg-[#FFD700] px-1">mind</span>, and{" "}
                <span className="bg-[#FFD700] px-1">body</span> for Christian
                service, leadership, and success in society.
              </p>
            </div>

            <div className="lg:col-span-5 reveal reveal-delay-2 flex flex-wrap gap-4 lg:justify-end items-start">
              <a
                href="#mission"
                className="btn-base btn-orange"
                data-testid="hero-cta-learn-more"
              >
                Learn More
                <ArrowDownRight size={14} />
              </a>
              <Link
                to="/basketball-camp"
                className="btn-base btn-ghost"
                data-testid="hero-cta-camp"
              >
                Camp Sign-Up
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          {/* Hero image strip — three equal squares filled edge-to-edge */}
          <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 reveal reveal-delay-2">
            <div className="photo-frame">
              <img
                src="/assets/other/community-4.png"
                alt="Divine Care Organization & Partners"
                className="w-full aspect-square object-cover photo-duotone"
              />
            </div>
            <div className="relative">
              <div className="photo-frame">
                <img
                  src="/assets/other/champions.jpg"
                  alt="Gold Coast Los Angeles Champions"
                  className="w-full aspect-square object-cover photo-duotone"
                />
              </div>
              <span
                className="sticker absolute -top-4 -left-3 z-10"
                style={{
                  background: "#FF5A1F",
                  color: "#F4EBDC",
                  borderColor: "#0E0E0E",
                  transform: "rotate(-3deg)",
                }}
                data-testid="champions-sticker"
              >
                LA CHAMPIONS
              </span>
            </div>
            <div className="relative">
              <div className="photo-frame">
                <img
                  src="/assets/other/community-1.jpg"
                  alt="Mentorship community"
                  className="w-full aspect-square object-cover photo-duotone"
                />
              </div>
              <span
                className="sticker sticker--orange absolute -bottom-4 -left-3 z-10"
                data-testid="hero-sticker"
              >
                <Sparkles size={12} /> SINCE DAY ONE
              </span>
            </div>
          </div>

          {/* Proverbs 1:4 scripture caption */}
          <div className="mt-8 max-w-3xl reveal reveal-delay-3">
            <span className="tag">SCRIPTURE · PROVERBS 1:4</span>
            <p className="mt-3 font-display italic text-2xl sm:text-3xl text-[#0E0E0E]/85 leading-snug">
              “For giving prudence to those who are simple, knowledge and
              discretion to the young.”
            </p>
          </div>
        </div>

        {/* Marquee — bottom of hero */}
        <div className="mt-14 sm:mt-20 bg-[#0E0E0E] text-[#F4EBDC] py-5 border-y-2 border-[#0E0E0E] overflow-hidden">
          <div className="marquee">
            <div className="marquee__track">
              {[...Array(2)].flatMap((_, k) =>
                marqueeItems.map((w, i) => (
                  <span
                    key={`${k}-${i}`}
                    className="font-display text-3xl sm:text-4xl"
                    style={{ color: w === "✱" ? "#1B4F72" : "#F4EBDC" }}
                  >
                    {w}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section
        id="mission"
        data-testid="mission-vision"
        className="py-24 sm:py-32 bg-[#F4EBDC] relative"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7 reveal">
              <span className="tag">MISSION</span>
              <h2 className="font-display text-[12vw] sm:text-[8vw] lg:text-[5.5vw] text-[#0E0E0E] mt-5 leading-[0.9]">
                TRAIN UP
                <br />
                <span className="text-[#1B4F72]">A CHILD</span> IN
                <br />
                THE WAY THEY
                <br />
                <span className="text-[#173E2A]">SHOULD GO</span>.
              </h2>
            </div>
            <p className="lg:col-span-5 text-base sm:text-lg leading-relaxed text-[#0E0E0E]/85 reveal reveal-delay-1">
              We meet young people where they are — in the city, in the church,
              on the court — and we walk with them all the way up. No one gets
              left behind. Every steward gets a path.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
            {/* Mission */}
            <article
              data-testid="mission-card"
              className="lg:col-span-7 panel p-8 sm:p-12 reveal"
            >
              <div className="flex items-center justify-between gap-6">
                <span className="font-mono text-[11px] tracking-[0.22em]">
                  / MISSION
                </span>
                <span className="big-num text-[6rem] sm:text-[8rem] leading-none">
                  M.
                </span>
              </div>
              <h3 className="font-display text-3xl sm:text-4xl mt-4 text-[#0E0E0E]">
                Spirit · Mind · Body.
              </h3>
              <p className="mt-6 text-[#0E0E0E]/80 leading-relaxed">
                Divine Care Organization focuses on youth development of the
                spirit, mind, and body for Christian service and leadership. We
                equip young people with life skills, spiritual guidance, mental
                health awareness, and personal discipline — through literature,
                sports training, mentorship, and character development.
              </p>
              <p className="mt-4 text-[#0E0E0E]/80 leading-relaxed">
                Helping youth become responsible, respectful, and well-rounded
                stewards prepared to serve God, their families, and their
                communities.
              </p>
            </article>

            {/* Vision */}
            <article
              data-testid="vision-card"
              className="lg:col-span-5 panel p-8 sm:p-12 reveal reveal-delay-1 text-[#F4EBDC]"
              style={{
                backgroundColor: "#0E0E0E",
                borderColor: "#0E0E0E",
                boxShadow: "6px 6px 0 #1B4F72",
              }}
            >
              <div className="flex items-center justify-between gap-6">
                <span className="font-mono text-[11px] tracking-[0.22em] text-[#F4EBDC]">
                  / VISION
                </span>
                <span className="big-num text-[6rem] sm:text-[8rem] leading-none text-[#F4EBDC]">
                  V.
                </span>
              </div>
              <h3 className="font-display text-3xl sm:text-4xl mt-4 text-[#F4EBDC]">
                Awareness · Respect · Integrity · Purpose.
              </h3>
              <p className="mt-6 text-[#F4EBDC]/85 leading-relaxed">
                We develop well-rounded young stewards who succeed in society
                with awareness, respect, integrity, and purpose.
              </p>
              <p className="mt-4 text-[#F4EBDC] leading-relaxed italic border-l-2 border-[#1B4F72] pl-4">
                When a child learns to truly care about what they do, why they
                do it, and how they do it — they will naturally learn to care
                for others in the same way.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section
        data-testid="programs-section"
        className="py-24 sm:py-32 bg-[#0E0E0E] text-[#F4EBDC] relative overflow-hidden"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-8 reveal">
            <div>
              <span className="tag" style={{ color: "#FFD700" }}>
                <span className="bg-[#FFD700] w-2 h-2 inline-block" />
                PROGRAMS
              </span>
              <h2 className="font-display text-[10vw] sm:text-[7vw] lg:text-[5vw] mt-5 leading-[0.9]">
                THREE LANES.
                <br />
                ONE <span className="text-[#FFD700]">PURPOSE</span>.
              </h2>
            </div>
            <p className="max-w-md text-[#F4EBDC]/75">
              Each program is a deliberate practice — pages turned, prayers
              said, sweat earned — where character is built and faith is formed.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {programs.map((p, i) => {
              const Icon = p.icon;
              return (
                <article
                  key={p.title}
                  data-testid={`program-card-${i}`}
                  className={`reveal reveal-delay-${i + 1} group bg-[#F4EBDC] text-[#0E0E0E] border-2 border-[#0E0E0E] flex flex-col`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden border-b-2 border-[#0E0E0E]">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 photo-duotone"
                      style={
                        p.title === "Cadet & Basketball"
                          ? { objectPosition: "center top" }
                          : undefined
                      }
                    />
                    <span className="sticker sticker--orange absolute top-4 left-4">
                      <Icon size={12} /> {p.tag}
                    </span>
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <div className="flex items-baseline justify-between">
                      <span className="big-num text-[5rem] sm:text-[6rem] leading-none">
                        {p.n}
                      </span>
                      <span className="font-mono text-[11px] tracking-[0.22em]">
                        / PROGRAM
                      </span>
                    </div>
                    <h3 className="font-display text-3xl mt-2 text-[#0E0E0E]">
                      {p.title.toUpperCase()}
                    </h3>
                    <p className="mt-4 text-[#0E0E0E]/80 leading-relaxed flex-1">
                      {p.description}
                    </p>
                    {p.href ? (
                      <Link
                        to={p.href}
                        className="mt-7 inline-flex items-center gap-2 font-mono text-xs tracking-[0.18em] uppercase font-bold text-[#1B4F72]"
                        data-testid={`program-card-cta-${i}`}
                      >
                        Explore Camp
                        <ArrowUpRight size={14} />
                      </Link>
                    ) : (
                      <a
                        href="#contact"
                        className="mt-7 inline-flex items-center gap-2 font-mono text-xs tracking-[0.18em] uppercase font-bold text-[#1B4F72]"
                        data-testid={`program-card-cta-${i}`}
                      >
                        Get Involved
                        <ArrowUpRight size={14} />
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOUNDER TEASE */}
      <section data-testid="founder-tease" className="py-24 sm:py-32 bg-[#F4EBDC]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 reveal relative">
            <div className="photo-frame">
              <img
                src="/assets/founder/trey-wideshot.png"
                alt="Trayshun D. Richmond, Founder"
                className="w-full object-cover aspect-[4/5] photo-duotone"
                style={{ objectPosition: "center 25%" }}
              />
            </div>
            <span
              className="sticker sticker--orange absolute -top-4 -right-3 z-10"
              data-testid="founder-tease-sticker"
            >
              FOUNDER · VESSEL
            </span>
          </div>
          <div className="lg:col-span-7 reveal reveal-delay-1">
            <span className="tag">FOUNDER · VESSEL</span>
            <h2 className="font-display text-[12vw] sm:text-[8vw] lg:text-[5.5vw] mt-5 leading-[0.9] text-[#0E0E0E]">
              A SERVANT
              <br />
              CALLED TO
              <br />
              <span className="text-[#1B4F72]">NURTURE</span>
              <br />
              PURE HEARTS.
            </h2>
            <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#0E0E0E]/85 max-w-2xl">
              Trayshun D. Richmond believes this work is a mission from the
              Lord — to protect, nurture, and guide the pure hearts of youth
              with faith, love, and purpose.
            </p>

            <div className="mt-10 panel-flat p-7 sm:p-9 bg-[#FFD700] border-2 border-[#0E0E0E] relative">
              <span className="font-mono text-[11px] tracking-[0.22em] absolute top-4 right-5 text-[#0E0E0E]/70">
                FOUNDER QUOTE / 01
              </span>
              <p className="font-display text-3xl sm:text-4xl text-[#0E0E0E] leading-tight mt-4">
                “LIFE IS LIKE A GARDEN, SO WE MUST TEND TO IT WELL, NURTURE IT
                WELL, AND CARE FOR IT INTENTLY.”
              </p>
              <p className="font-mono text-xs tracking-[0.22em] mt-6 text-[#0E0E0E]">
                — T. RICHMOND
              </p>
            </div>

            <div className="mt-10">
              <Link
                to="/founder"
                className="btn-base btn-ink"
                data-testid="founder-tease-cta"
              >
                Read Founder's Story
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA + CONTACT */}
      <section
        id="contact"
        data-testid="contact-section"
        className="bg-[#1B4F72] text-[#F4EBDC] py-24 sm:py-32 relative overflow-hidden"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 reveal">
            <span className="tag" style={{ color: "#FFD700" }}>
              <span className="bg-[#FFD700] w-2 h-2 inline-block" />
              JOIN THE MISSION
            </span>
            <h2 className="font-display text-[12vw] sm:text-[8vw] lg:text-[5.5vw] mt-5 leading-[0.9]">
              HELP US BUILD
              <br />
              THE NEXT
              <br />
              GENERATION OF
              <br />
              <span className="bg-[#FFD700] text-[#1B4F72] px-3 py-1 inline-block mt-2">
                STEWARDS.
              </span>
            </h2>
            <p className="mt-8 leading-relaxed max-w-md text-[#F4EBDC]/90">
              Support Divine Care Organization as we help youth grow in faith,
              leadership, discipline, awareness, respect, and integrity.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="border-2 border-[#0E0E0E] p-5 bg-[#F4EBDC] text-[#0E0E0E]">
                <p className="font-mono text-[11px] tracking-[0.22em]">PHONE</p>
                <a
                  href="tel:+14246107795"
                  className="font-display text-2xl sm:text-3xl mt-2 block"
                  data-testid="contact-phone"
                >
                  424-610-7795
                </a>
              </div>
              <div className="border-2 border-[#0E0E0E] p-5 bg-[#F4EBDC] text-[#0E0E0E]">
                <p className="font-mono text-[11px] tracking-[0.22em]">EMAIL</p>
                <a
                  href="mailto:ydevelopment7@gmail.com"
                  className="font-display text-base sm:text-lg mt-2 block break-all"
                  data-testid="contact-email"
                >
                  ydevelopment7@gmail.com
                </a>
              </div>
            </div>

            <div
              data-testid="zelle-block"
              className="mt-5 border-2 border-[#0E0E0E] p-5 bg-[#FFD700] text-[#0E0E0E]"
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-mono text-[11px] tracking-[0.22em]">
                    SUPPORT VIA ZELLE
                  </p>
                  <a
                    href="mailto:YDevelopment7@gmail.com"
                    className="font-display text-xl sm:text-2xl mt-2 block break-all"
                    data-testid="zelle-email"
                  >
                    YDevelopment7@gmail.com
                  </a>
                </div>
                <span className="font-mono text-[10px] tracking-[0.22em] bg-[#0E0E0E] text-[#FFD700] px-3 py-2">
                  $ TAX-DEDUCTIBLE
                </span>
              </div>
              <p className="font-mono text-[11px] tracking-[0.16em] uppercase mt-3 text-[#0E0E0E]/75">
                Send any amount through Zelle to support youth programs, books, uniforms, and camp scholarships.
              </p>
            </div>
          </div>
          <div className="lg:col-span-7 reveal reveal-delay-1">
            <div className="bg-[#F4EBDC] text-[#0E0E0E] border-2 border-[#0E0E0E] p-7 sm:p-10">
              <span className="tag">/ REACH OUT</span>
              <h3 className="font-display text-3xl sm:text-4xl mt-3">
                Tell us how we can serve, partner, or pray with you.
              </h3>
              <div className="mt-8">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
